import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FileStack, Plus, Loader2, X, Download, FileText, Clock,
} from 'lucide-react'
import { apiGet, apiPost } from '@/lib/api'
import { cn, formatDate } from '@/lib/utils'

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1'

export function ReportsPage() {
  const queryClient = useQueryClient()
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)

  // ── Queries ─────────────────────────────────────────────────────────────
  const { data: reports, isLoading } = useQuery({
    queryKey: ['docflow-reports'],
    queryFn: () => apiGet<any>('/docflow/reports'),
  })

  const { data: templates } = useQuery({
    queryKey: ['docflow-report-templates'],
    queryFn: () => apiGet<any[]>('/docflow/reports/templates'),
  })

  // ── Generate report ───────────────────────────────────────────────────
  const [genForm, setGenForm] = useState({
    template_id: '', title: '', source_type: 'compliance', source_id: '',
  })

  const generateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/docflow/reports/generate', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docflow-reports'] })
      setShowGenerateModal(false)
      setGenForm({ template_id: '', title: '', source_type: 'compliance', source_id: '' })
    },
  })

  // ── Create template ───────────────────────────────────────────────────
  const [tplForm, setTplForm] = useState({
    name: '', include_toc: true, include_register: true, page_size: 'A4',
    cover_title: '', cover_subtitle: '', primary_colour: '#166bf2',
  })

  const createTemplateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/docflow/reports/templates', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docflow-report-templates'] })
      setShowTemplateModal(false)
      setTplForm({ name: '', include_toc: true, include_register: true, page_size: 'A4', cover_title: '', cover_subtitle: '', primary_colour: '#166bf2' })
    },
  })

  const reportList = reports?.reports ?? (Array.isArray(reports) ? reports : [])
  const templateList = Array.isArray(templates) ? templates : []

  function handleDownload(reportId: string, title: string) {
    const stored = localStorage.getItem('estatekey-auth')
    const token = stored ? JSON.parse(stored).state?.accessToken : null
    if (!token) return

    const url = `${API_BASE}/docflow/reports/${reportId}/download`
    const a = document.createElement('a')
    a.href = url
    a.download = `${title}.pdf`
    // Use fetch for auth header
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        a.href = URL.createObjectURL(blob)
        a.click()
      })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-800">Report Packs</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Generate branded compliance packs, due diligence bundles, and audit evidence
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowTemplateModal(true)} className="btn-secondary">
            <Plus className="w-4 h-4" /> Template
          </button>
          <button onClick={() => setShowGenerateModal(true)} className="btn-primary">
            <FileStack className="w-4 h-4" /> Generate Report
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="card py-4">
          <p className="text-xs text-gray-400">Generated Reports</p>
          <p className="text-2xl font-bold text-navy-800">{reportList.length}</p>
        </div>
        <div className="card py-4">
          <p className="text-xs text-gray-400">Templates</p>
          <p className="text-2xl font-bold text-navy-800">{templateList.length}</p>
        </div>
        <div className="card py-4">
          <p className="text-xs text-gray-400">Source Types</p>
          <p className="text-sm font-medium text-navy-800 mt-1">Compliance, Case, Site</p>
        </div>
      </div>

      {/* Templates */}
      {templateList.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase mb-3">Templates</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {templateList.map((tpl: any) => (
              <div key={tpl.id} className="card py-3 px-4">
                <h3 className="text-sm font-medium text-navy-800">{tpl.name}</h3>
                <div className="flex gap-2 mt-2">
                  {tpl.include_toc && <span className="badge text-xs bg-gray-100 text-gray-600">TOC</span>}
                  {tpl.include_register && <span className="badge text-xs bg-gray-100 text-gray-600">Register</span>}
                  <span className="badge text-xs bg-gray-100 text-gray-600">{tpl.page_size ?? 'A4'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reports */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase mb-3">Generated Reports</h2>
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-800" />
          </div>
        ) : reportList.length === 0 ? (
          <div className="card text-center py-16">
            <FileStack className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No reports generated yet</p>
            <p className="text-xs text-gray-400 mt-1">Use &ldquo;Generate Report&rdquo; to create a compliance pack or evidence bundle</p>
          </div>
        ) : (
          <div className="space-y-2">
            {reportList.map((report: any) => (
              <div key={report.id} className="card flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-navy-800 truncate">{report.title}</h3>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-gray-400">{formatDate(report.created_at)}</span>
                    {report.source_type && (
                      <span className="badge text-xs bg-gray-100 text-gray-600">{report.source_type}</span>
                    )}
                    {report.status && (
                      <span className={cn('badge text-xs',
                        report.status === 'ready' ? 'bg-green-100 text-green-700'
                          : report.status === 'generating' ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-600'
                      )}>
                        {report.status === 'generating' && <Clock className="w-3 h-3 mr-1 inline" />}
                        {report.status}
                      </span>
                    )}
                  </div>
                </div>
                {(report.status === 'ready' || !report.status) && (
                  <button
                    onClick={() => handleDownload(report.id, report.title)}
                    className="btn-secondary text-sm"
                  >
                    <Download className="w-4 h-4" /> Download
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-navy-800 flex items-center gap-2">
                <FileStack className="w-5 h-5 text-blue-600" />
                Generate Report Pack
              </h2>
              <button onClick={() => setShowGenerateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (!genForm.template_id || !genForm.title || !genForm.source_id) return
                generateMutation.mutate(genForm)
              }}
              className="space-y-4"
            >
              <div>
                <label className="label">Template *</label>
                <select
                  value={genForm.template_id}
                  onChange={(e) => setGenForm({ ...genForm, template_id: e.target.value })}
                  className="input" required
                >
                  <option value="">Select template...</option>
                  {templateList.map((tpl: any) => (
                    <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Report Title *</label>
                <input
                  value={genForm.title}
                  onChange={(e) => setGenForm({ ...genForm, title: e.target.value })}
                  className="input" placeholder="e.g. Rockliffe Hall — Q1 2026 Compliance Pack" required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Source Type</label>
                  <select
                    value={genForm.source_type}
                    onChange={(e) => setGenForm({ ...genForm, source_type: e.target.value })}
                    className="input"
                  >
                    <option value="compliance">Compliance</option>
                    <option value="case">Case</option>
                    <option value="site">Site</option>
                  </select>
                </div>
                <div>
                  <label className="label">Source ID *</label>
                  <input
                    value={genForm.source_id}
                    onChange={(e) => setGenForm({ ...genForm, source_id: e.target.value })}
                    className="input" placeholder="Site or Case ID" required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowGenerateModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={!genForm.template_id || !genForm.title || !genForm.source_id || generateMutation.isPending} className="btn-primary">
                  {generateMutation.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><FileStack className="w-4 h-4" /> Generate</>}
                </button>
              </div>
              {generateMutation.isError && <p className="text-sm text-red-600">Failed to generate report. Check DocFlow connection.</p>}
            </form>
          </div>
        </div>
      )}

      {/* Create Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-navy-800 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                New Report Template
              </h2>
              <button onClick={() => setShowTemplateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (!tplForm.name) return
                createTemplateMutation.mutate({
                  name: tplForm.name,
                  include_toc: tplForm.include_toc,
                  include_register: tplForm.include_register,
                  page_size: tplForm.page_size,
                  ...(tplForm.cover_title && {
                    cover_page_config: {
                      title: tplForm.cover_title,
                      ...(tplForm.cover_subtitle && { subtitle: tplForm.cover_subtitle }),
                    },
                  }),
                  branding: { primary_colour: tplForm.primary_colour },
                })
              }}
              className="space-y-4"
            >
              <div>
                <label className="label">Template Name *</label>
                <input
                  value={tplForm.name}
                  onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })}
                  className="input" placeholder="e.g. Compliance Audit Pack" required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Cover Page Title</label>
                  <input
                    value={tplForm.cover_title}
                    onChange={(e) => setTplForm({ ...tplForm, cover_title: e.target.value })}
                    className="input" placeholder="Property Compliance Pack"
                  />
                </div>
                <div>
                  <label className="label">Subtitle</label>
                  <input
                    value={tplForm.cover_subtitle}
                    onChange={(e) => setTplForm({ ...tplForm, cover_subtitle: e.target.value })}
                    className="input" placeholder="Prepared by EstateKey"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="label">Page Size</label>
                  <select value={tplForm.page_size} onChange={(e) => setTplForm({ ...tplForm, page_size: e.target.value })} className="input">
                    <option value="A4">A4</option>
                    <option value="Letter">Letter</option>
                  </select>
                </div>
                <div>
                  <label className="label">Brand Colour</label>
                  <input type="color" value={tplForm.primary_colour} onChange={(e) => setTplForm({ ...tplForm, primary_colour: e.target.value })} className="input h-10" />
                </div>
                <div className="space-y-2 pt-5">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={tplForm.include_toc} onChange={(e) => setTplForm({ ...tplForm, include_toc: e.target.checked })} />
                    Table of Contents
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={tplForm.include_register} onChange={(e) => setTplForm({ ...tplForm, include_register: e.target.checked })} />
                    Document Register
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowTemplateModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={!tplForm.name || createTemplateMutation.isPending} className="btn-primary">
                  {createTemplateMutation.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : <><Plus className="w-4 h-4" /> Create Template</>}
                </button>
              </div>
              {createTemplateMutation.isError && <p className="text-sm text-red-600">Failed to create template.</p>}
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
